import './applyApplicationTypePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const { applicationType, setApplicationType } = useApplyStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      applicationTypeValue: applicationType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<ApplicationType>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const applicationTypeValue = event.target.value;

    setIsInitialSelection(isEmpty(applicationType) && isValidClickEvent(event));

    setApplicationType(applicationTypeValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { applicationTypeValue } = values;

    if (applicationTypeValue) {
      navigate(next());
    }
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <div className="apply-application-type-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applyApplicationType.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applyApplicationType.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.applyApplicationType.header.drawerButton
              }
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applyApplicationType.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.applyApplicationType.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applyApplicationType.form.h1}>
                  {t('ApplyApplicationTypePage.title')}
                </h1>
                <h2 data-testid={locator.pages.applyApplicationType.form.h2}>
                  {t('ApplyApplicationTypePage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <RadioGroup
                    defaultValue={applicationType}
                    name="applicationTypeValue"
                    options={[
                      {
                        label: t('ApplyApplicationTypePage.refinance'),
                        value: ApplicationType.REFINANCE,
                        testId:
                          locator.pages.applyApplicationType.form.radioOption1,
                      },
                      {
                        label: t('ApplyApplicationTypePage.leaseBuyout'),
                        value: ApplicationType.LEASE_BUYOUT,
                        testId:
                          locator.pages.applyApplicationType.form.radioOption2,
                      },
                      {
                        label: t('ApplyApplicationTypePage.purchase'),
                        value: ApplicationType.PURCHASE,
                        testId:
                          locator.pages.applyApplicationType.form.radioOption3,
                      },
                    ]}
                    register={register}
                    testId={
                      locator.pages.applyApplicationType.form.radioGroupRoot
                    }
                    watch={watch}
                    onClick={radioOptionClicked}
                  />
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.applyApplicationType.form.submitButton
                    }
                    disabled={isEmpty(applicationType)}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplyApplicationTypePage.continue')}
                  </LoadingButton>
                </div>
              </div>
              <footer
                data-testid={locator.pages.applyApplicationType.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyApplicationTypePage';
